import type { Component } from "solid-js";
import { getOwner, Show } from "solid-js";
import { Navigate, Route, Router, useParams, useSearchParams } from "@solidjs/router";
import RouteGuard from "./pages/RouteGuard";
import LoginCallbackPage from "./pages/LoginCallbackPage";
import FormEditorPage, { preLoad as formEditorPagePreLoad } from "./pages/formEditor/FormEditorPage";
import CustomerLoader from "./pages/CustomerLoader";
import OverviewPage from "./pages/customer/OverviewPage";
import UserService from "./services/UserService";
import LogoutCallbackPage from "./pages/LogoutCallbackPage";
import CrowdinInContextPage from "./pages/CrowdinInContextPage";
import { handleImpersonation } from "./pages/pagePreLoaders";
import { Title } from "@solidjs/meta";
import { withSentryRouterRouting } from "@sentry/solid/solidrouter";
import SentryService from "./services/SentryService";
import PostHogService from "./services/PostHogService";
import TrustService from "./services/TrustService";
import { setOwner } from "./owner";
import { FormEditorRoute } from "./pages/formEditor/FormEditorContent";
import Layout from "./pages/customer/layout/Layout";
import FolderPage from "./pages/customer/FolderPage";
import FormPage, { FormPageWithoutFolderId } from "./pages/customer/FormPage";
import environment from "./environment";
import DeveloperPage from "./pages/DeveloperPage";
import NotFoundPage from "./pages/NotFoundPage";
import HomePage from "./pages/HomePage";
import UserProfilePage from "./pages/customer/settings/UserProfilePage";
import CompanyInformationPage from "./pages/customer/settings/CompanyInformationPage";
import BillingOverviewPage from "./pages/customer/settings/billing/overview/BillingOverviewPage.tsx";
import InvoicesPage from "./pages/customer/settings/billing/InvoicesPage";
import UsagePage from "./pages/customer/settings/billing/UsagePage.tsx";
import ApiKeysPage from "./pages/customer/settings/ApiKeysPage";
import WebhooksPage from "./pages/customer/settings/WebhooksPage";
import WebhookPage from "./pages/customer/settings/WebhookPage";
import EmailConfigurationPage from "./pages/customer/settings/EmailConfigurationPage";
import ServiceAccountsPage from "./pages/customer/settings/ServiceAccountsPage";
import SsoPage from "./pages/customer/settings/SsoPage";
import CustomerService from "./services/CustomerService";
import { ModalManagerProvider } from "./components/modal/ModalManager";
import OAuth2CallbackPage from "./pages/OAUth2CallbackPage";
import UsersPage from "./pages/customer/iam/UsersPage";
import UserPage from "./pages/customer/iam/UserPage";
import GroupsPage from "./pages/customer/iam/GroupsPage";
import GroupPage from "./pages/customer/iam/GroupPage";
import RolesPage from "./pages/customer/iam/RolesPage";
import DataSourcesPage from "./pages/customer/DataSourcesPage";
import DataSourcePage from "./pages/customer/DataSourcePage";
import InviteAcceptancePage from "./pages/invites/InviteAcceptancePage.tsx";
import InviteService from "./services/InviteService";
import OnboardingPage from "./pages/onboarding/OnboardingPage.tsx";
import SignupService from "./services/SignupService.ts";
import SignupPage from "./pages/signup/SignupPage.tsx";
import EmailValidationPage from "./pages/signup/EmailValidationPage.tsx";

const LegacyFormOverviewRedirect: Component = () => {
  const params = useParams<{ customerId: string }>();

  return <Navigate href={`/customers/${params.customerId}`} />;
};

const LegacyFormSubmissionsRedirect: Component = () => {
  const params = useParams<{ customerId: string }>();
  const [searchParams] = useSearchParams<{ formId: string }>();

  return (
    <Navigate
      href={
        searchParams.formId
          ? `/customers/${params.customerId}/forms/${searchParams.formId}`
          : `/customers/${params.customerId}`
      }
    />
  );
};

const App: Component = () => {
  setOwner(getOwner());
  UserService.init();
  CustomerService.init();
  SignupService.init();
  InviteService.init();
  SentryService.init();
  PostHogService.init();
  TrustService.init();

  const SentryRouter = withSentryRouterRouting(Router);

  return (
    <>
      <Title>MoreApp</Title>
      <SentryRouter>
        <Show when={environment.isDevelopment()}>
          <Route path="/crowdin" component={CrowdinInContextPage} />
        </Show>
        <Route path="/login/callback" component={LoginCallbackPage} />
        <Route path="/logout/callback" component={LogoutCallbackPage} />
        <Route path="/oauth2/callback" component={OAuth2CallbackPage} />

        {/* Signup and Onboarding */}
        <Route path="/signup" component={SignupPage} />
        <Route path="/email-validation/:token" component={EmailValidationPage} />
        <Route path="/invites/:inviteToken" component={InviteAcceptancePage} />

        {/* Legacy platform deep links */}
        <Route path="/login" component={() => <Navigate href="/" />} />
        <Route path="/customers/:customerId/form/overview" component={LegacyFormOverviewRedirect} />
        <Route path="/customers/:customerId/form/submissions" component={LegacyFormSubmissionsRedirect} />

        <Route path="*" component={NotFoundPage} />

        <Route
          path="/"
          component={RouteGuard}
          preload={({ location }) => {
            handleImpersonation(location);
          }}
        >
          <Route path="/" component={HomePage} />
          <Route path="/developer" component={DeveloperPage} />
          <Route path="/customers/:customerId" matchFilters={{ customerId: /^[1-9]\d*$/ }} component={CustomerLoader}>
            <Route path="/" component={ModalManagerProvider}>
              <Route path="/onboarding" component={OnboardingPage} />

              <Route path="/" component={Layout}>
                <Route path="/" component={OverviewPage} />

                {/* Data sources */}
                <Route path="/data-sources" component={DataSourcesPage} />
                <Route path="/data-sources/:dataSourceId" component={DataSourcePage} />

                {/* IAM */}
                <Route path="/users" component={UsersPage} />
                <Route path="/users/:userId" component={UserPage} />
                <Route path="/groups" component={GroupsPage} />
                <Route path="/groups/:groupId" component={GroupPage} />
                <Route path="/roles" component={RolesPage} />

                {/* Forms and folders */}
                <Route path="/folders/:folderId" component={FolderPage} />
                <Route path="/folders/:folderId/forms/:formId" component={FormPage} />
                <Route path="/forms/:formId" component={FormPageWithoutFolderId} />

                {/* Account settings */}
                <Route path="/settings/profile" component={UserProfilePage} />
                <Route path="/settings/company" component={CompanyInformationPage} />

                {/* Billing */}
                <Route path="/settings/billing" component={BillingOverviewPage} />
                <Route path="/settings/invoices" component={InvoicesPage} />
                <Route path="/settings/usage" component={UsagePage} />

                {/* Connection settings */}
                <Route path="/settings/api-keys" component={ApiKeysPage} />
                <Route path="/settings/webhooks" component={WebhooksPage} />
                <Route path="/settings/webhooks/:webhookId" component={WebhookPage} />
                <Route path="/settings/email-configuration" component={EmailConfigurationPage} />
                <Route path="/settings/service-accounts" component={ServiceAccountsPage} />
                <Route path="/settings/sso" component={SsoPage} />
              </Route>

              <Route path="/forms/:formId">
                <Route path="/formVersions/:formVersionId">
                  <Route
                    path={[
                      FormEditorRoute.FIELDS,
                      FormEditorRoute.SUBFORMS,
                      FormEditorRoute.RULES,
                      FormEditorRoute.INTEGRATIONS,
                      FormEditorRoute.SETTINGS,
                      FormEditorRoute.VARIABLES,
                    ]}
                    component={FormEditorPage}
                    preload={formEditorPagePreLoad}
                  />
                  <Route path="*" component={() => <Navigate href={FormEditorRoute.FIELDS} />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
      </SentryRouter>
    </>
  );
};

export default App;
